import { Gym } from 'fitbeat.models';
import { GymState } from '../entities/gymschedule';
import { UserWorkoutSession } from '../entities/workoutsession';
import { GymScheduler } from '../logic/gymscheduling';
import { DbGymRepository } from '../persistence/repositories';

export class Metrics {
  private gymScheduler: GymScheduler;

  constructor(gymScheduler: GymScheduler, private gym: Gym) {
    this.gymScheduler = gymScheduler;
  }

  // Overall
  public async avgOverallQueueTimeBeforeFirstSessionInSeconds(fromDate: Date, toDate: Date): Promise<number> {
    return this.avgQueueTimeBeforeFirstSessionInSeconds(await this.getAllSessions(fromDate, toDate));
  }

  public async maxOverallQueueTimeBeforeFirstSessionInSeconds(fromDate: Date, toDate: Date): Promise<number> {
    return this.maxQueueTimeBeforeFistSessionInSeconds(await this.getAllSessions(fromDate, toDate));
  }

  public async countOverallSessions(fromDate: Date, toDate: Date): Promise<number> {
    return this.countSessions(await this.getAllSessions(fromDate, toDate));
  }

  public async countPhantoms(fromDate: Date, toDate: Date): Promise<number> {
    return this.countSessions(
      (await this.getAllSessions(fromDate, toDate)).filter((session: UserWorkoutSession) => session.removedAt),
    );
  }

  public avgCapacity(fromDate: Date, toDate: Date): Promise<number> {
    // todo: consider collapsing avgCapacity, maxCapacity and minCapacity into one method
    let sum = 0;
    let count = 0;
    return this.cyclesBetweenDates(fromDate, toDate)
      .then((map: Map<number, GymState>) => {
        map.forEach((gymState: GymState, timeKey: number) => {
          count++;
          sum += gymState.capacity();
        });

        return sum / count;
      });
  }

  public maxCapacity(fromDate: Date, toDate: Date): Promise<number> {
    // todo: consider collapsing avgCapacity, maxCapacity and minCapacity into one method
    let max = -1;
    return this.cyclesBetweenDates(fromDate, toDate)
      .then((map: Map<number, GymState>) => {
        map.forEach((gymState: GymState, timeKey: number) => {
          if (gymState.capacity() > max) {
            max = gymState.capacity();
          }
        });

        return max;
      });
  }

  public minCapacity(fromDate: Date, toDate: Date): Promise<number> {
    // todo: consider collapsing avgCapacity, maxCapacity and minCapacity into one method
    let min = 1;
    return this.cyclesBetweenDates(fromDate, toDate)
      .then((map: Map<number, GymState>) => {
        map.forEach((gymState: GymState, timeKey: number) => {
          if (gymState.capacity() < min) {
            min = gymState.capacity();
          }
        });

        return min;
      });
  }

  // Singles
  public async avgSingleQueueTimeBeforeFirstSessionInSeconds(fromDate: Date, toDate: Date): Promise<number> {
    return this.avgQueueTimeBeforeFirstSessionInSeconds(await this.getSingleSessions(fromDate, toDate));
  }

  public async maxSingleQueueTimeBeforeFirstSessionInSeconds(fromDate: Date, toDate: Date): Promise<number> {
    return this.maxQueueTimeBeforeFistSessionInSeconds(await this.getSingleSessions(fromDate, toDate));
  }

  public async countSingleSessions(fromDate: Date, toDate: Date): Promise<number> {
    return this.countSessions(await this.getSingleSessions(fromDate, toDate));
  }

  // Couples
  public async avgCoupleQueueTimeBeforeFirstSessionInSeconds(fromDate: Date, toDate: Date): Promise<number> {
    return this.avgQueueTimeBeforeFirstSessionInSeconds(await this.getCoupleSessions(fromDate, toDate));
  }

  public async maxCoupleQueueTimeBeforeFirstSessionInSeconds(fromDate: Date, toDate: Date): Promise<number> {
    return this.maxQueueTimeBeforeFistSessionInSeconds(await this.getCoupleSessions(fromDate, toDate));
  }

  public async countCoupleSessions(fromDate: Date, toDate: Date): Promise<number> {
    return this.countSessions(await this.getCoupleSessions(fromDate, toDate));
  }

  // Helpers
  private countSessions(sessions: UserWorkoutSession[]): number {
    return sessions.length;
  }

  private avgQueueTimeBeforeFirstSessionInSeconds(sessions: UserWorkoutSession[]): number {
    let sum = 0;
    let count = 0;
    sessions.forEach((session: UserWorkoutSession) => {
      count++;
      sum += session.getWaitTimeInSeconds();
    });
    return sum / count;
  }

  private maxQueueTimeBeforeFistSessionInSeconds(sessions: UserWorkoutSession[]): number {
    let max = -1;
    sessions.forEach((session: UserWorkoutSession) => {
      if (session.getWaitTimeInSeconds() > max) {
        max = session.getWaitTimeInSeconds();
      }
    });
    return max;
  }

  private async getAllSessions(fromDate: Date, toDate: Date): Promise<UserWorkoutSession[]> {
    const workouts = await this.gymScheduler.workoutRepository.getAll(DbGymRepository.getTestGym());
    return workouts.filter(
      (session: UserWorkoutSession) => (session.earliestAllowedStartTime >= fromDate
          && session.earliestAllowedStartTime <= toDate),
    );
  }

  private async getSingleSessions(fromDate: Date, toDate: Date): Promise<UserWorkoutSession[]> {
    const workouts = await this.gymScheduler.workoutRepository.getAll(DbGymRepository.getTestGym());
    return workouts.filter(
      (session: UserWorkoutSession) => (!UserWorkoutSession.hasPairedWorkoutSession(session)
          && (session.earliestAllowedStartTime >= fromDate
            && session.earliestAllowedStartTime <= toDate)),
    );
  }

  private async getCoupleSessions(fromDate: Date, toDate: Date): Promise<UserWorkoutSession[]> {
    const workouts = await this.gymScheduler.workoutRepository.getAll(DbGymRepository.getTestGym());
    return workouts.filter(
      (session: UserWorkoutSession) => (UserWorkoutSession.hasPairedWorkoutSession(session)
          && (session.earliestAllowedStartTime >= fromDate
            && session.earliestAllowedStartTime <= toDate)),
    );
  }

  private async cyclesBetweenDates(fromDate: Date, toDate: Date): Promise<Map<number, GymState>> {
    return this.gymScheduler.futureCyclesFromDate(this.gym, fromDate)
      .then((map: Map<number, GymState>) => {
        const returnMap: Map<number, GymState> = new Map();
        map.forEach((gymState: GymState, timeKey: number) => {
          if (timeKey <= toDate.getTime()) {
            returnMap.set(timeKey, gymState);
          }
        });
        return returnMap;
      });
  }
}
